import React, { FunctionComponent, useCallback, useContext, useMemo } from "react";
import { Grid, makeStyles, Theme } from "@material-ui/core";
import { AnswerContext } from "./context/answer-context";
import { FeedbackType } from "../../../api/smartmelts-api/graphql/generated";
import { NutrientsAnswer } from "./nutrients-answer";
import { IntroductionAnswer } from "./introduction-answer";
import { MicronutrientsAnswer } from "./micronutrients-answer";
import { ExplanationWrapper } from "../explanation/explanation-wrapper";
import { Explanation } from "../../../models/types/category-types";

import { AnswerWrapperFooter } from "./answer-wrapper-footer";
import { GenesAnswer } from "./genes-answer";
import { StoryDataRecommendation } from "./story-data-recommendation";
import { NutrientsToDecreaseAnswer } from "./nutrients-to-decrease-answer";
import { NutrientsToIncreaseAnswer } from "./nutrients-to-increase-answer";
import { ResilienceAnswer } from "./resilience/resilience-answer";
import { GeneInfoAnswer } from "./gene-info-answer";
import { GeneRiskInfoAnswer } from "./gene-risk-info-answer";
import { QuestionSectionLoadingIndicator } from "../question/question-section-loading-indicator";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    minHeight: "100%",
  },
}));

interface IAnswerSectionProps {}

export const AnswerSection: FunctionComponent<IAnswerSectionProps> = (props) => {
  const classes = useStyles();
  const { questions, activeAnswerIndex, setActiveAnswerIndex } = useContext(AnswerContext);

  const setAnswerIndex = useCallback(
    (index: number) => {
      if (index < 0) {
        setActiveAnswerIndex(0);
      } else if (index > questions.length - 1) {
        setActiveAnswerIndex(questions.length - 1);
      } else setActiveAnswerIndex(index);
    },
    [setActiveAnswerIndex, questions],
  );

  const AnswerComponent = useMemo(() => {
    const activeAnswer = questions[activeAnswerIndex];
    switch (activeAnswer?.feedbackType) {
      case FeedbackType.STORYDATA:
      case FeedbackType.STORYRECOMMENDATION:
        return StoryDataRecommendation;
      case FeedbackType.GENES:
        return GenesAnswer;
      case FeedbackType.NUTRIENTS_TO_DECREASE:
        return NutrientsToDecreaseAnswer;
      case FeedbackType.NUTRIENTS_TO_INCREASE:
        return NutrientsToIncreaseAnswer;
      case FeedbackType.NUTRIENTS:
        return NutrientsAnswer;
      case FeedbackType.INTRODUCTION:
        return IntroductionAnswer;
      case FeedbackType.MICRONUTRIENTS:
        return MicronutrientsAnswer;
      case FeedbackType.RESILIENCE:
        return ResilienceAnswer;
      case FeedbackType.GENE_INFO:
        return GeneInfoAnswer;
      case FeedbackType.GENE_RISK_INFO:
        return GeneRiskInfoAnswer;
      default:
        return () => <QuestionSectionLoadingIndicator />;
    }
  }, [activeAnswerIndex, questions]);

  const explanation = useMemo(() => {
    const activeAnswer = questions[activeAnswerIndex];
    switch (activeAnswer?.feedbackType) {
      case FeedbackType.NUTRIENTS:
      case FeedbackType.NUTRIENTS_TO_DECREASE:
      case FeedbackType.NUTRIENTS_TO_INCREASE:
        return Explanation.NUTRIENT;
      default:
        return undefined;
    }
  }, [activeAnswerIndex, questions]);

  return (
    <ExplanationWrapper
      onPrevious={() => setAnswerIndex(activeAnswerIndex - 1)}
      explanation={explanation}
      activeIndex={activeAnswerIndex}
      pages={questions.length}
      setIndex={setAnswerIndex}
    >
      <Grid container direction="column" className={classes.container}>
        <Grid xs item>
          <AnswerComponent />
        </Grid>
        <Grid xs item>
          <AnswerWrapperFooter
            activeIndex={activeAnswerIndex}
            pages={questions.length}
            setAnswerIndex={setAnswerIndex}
          />
        </Grid>
      </Grid>
    </ExplanationWrapper>
  );
};
